import sm2Util from './sm2Util'

interface Options {
  expire?: number
  encrypt?: boolean
}

interface Stored {
  data: string
  expire: number
}

export class CacheDB {
  private dbName: string
  private storeName: string
  private version: number
  private db: IDBDatabase | null = null

  constructor(
    dbName = '__cacheUtil_db',
    storeName = '__cacheUtil_store',
    version = 1
  ) {
    this.dbName = dbName
    this.storeName = storeName
    this.version = version
  }

  // 显式初始化，或者调用任何方法时自动调用
  async init(): Promise<IDBDatabase> {
    if (this.db) return this.db

    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version)

      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName)
        }
      }

      req.onsuccess = () => {
        this.db = req.result
        resolve(this.db)
      }

      req.onerror = () => reject(req.error)
    })
  }

  async set(key: string, value: any, options: Options = {}): Promise<boolean> {
    const { expire = 0, encrypt = false } = options
    let str = typeof value === 'string' ? value : JSON.stringify(value)
    if (encrypt) {
      const enc = sm2Util.encrypt(str)
      if (!enc) return false
      str = enc
    }
    const expireAt = expire > 0 ? Date.now() + expire : 0
    const wrapped: Stored = { data: str, expire: expireAt }
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite')
      tx.objectStore(this.storeName).put(wrapped, key)
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => reject(tx.error)
    })
  }

  async get(key: string, options: Options = {}): Promise<any> {
    const { encrypt = false } = options
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly')
      const req = tx.objectStore(this.storeName).get(key)
      req.onsuccess = async () => {
        const wrapped: Stored = req.result
        if (!wrapped) return resolve(null)
        if (wrapped.expire && wrapped.expire < Date.now()) {
          await this.remove(key) // 过期自动删除
          return resolve(null)
        }
        let str = wrapped.data
        if (encrypt) {
          const dec = sm2Util.decrypt(str)
          if (dec === null) return resolve(null)
          str = dec
        }
        try {
          resolve(JSON.parse(str))
        } catch {
          resolve(str)
        }
      }
      req.onerror = () => reject(req.error)
    })
  }

  async remove(key: string): Promise<boolean> {
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite')
      tx.objectStore(this.storeName).delete(key)
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => reject(tx.error)
    })
  }

  async clear(): Promise<boolean> {
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite')
      tx.objectStore(this.storeName).clear()
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => reject(tx.error)
    })
  }
}
